import { timeService } from "./time-service";
import { EisenhowerMatrix, PriorityAnalysis } from "./eisenhower-matrix";
import { bodyDoublingService, BodyDoublingSession } from "./body-doubling-service";
import { hyperfocusBreakService, BreakType } from "./hyperfocus-break-service";
import { energyContextService, EnergyLevel, EnergyContext } from "./energy-context-service";
import { analytics } from "./analytics";
import { timeEstimationEngine, TimeEstimationMode, EstimationResult } from "./time-estimation";

export interface TodoItem {
  id: string;
  title: string;
  description?: string;
  priority: "low" | "medium" | "high" | "urgent";
  status: "pending" | "in_progress" | "completed" | "blocked";
  created_at: string;
  due_date?: string;
  project_id?: string;
  tags: string[];
  estimated_minutes?: number;
  notes: string;
  // Time estimation fields
  time_estimation_mode?: TimeEstimationMode;
  original_estimate?: number;
  estimation_confidence?: number;
  suggested_breaks?: number[];
  estimation_explanation?: string;
  actual_minutes?: number; // Tracked for learning
  // Automatic prioritization fields
  importance_level?: number; // 1-10 scale for objective importance
  urgency_level?: number; // 1-10 scale for time sensitivity
  eisenhower_quadrant?: 1 | 2 | 3 | 4; // 1: Do First, 2: Schedule, 3: Delegate, 4: Delete
  auto_prioritized?: boolean; // Flag to track AI-assigned priorities
  priority_confidence?: number; // 0-100 confidence score
  priority_reasoning?: string[]; // Human-readable reasons for the priority
  adhd_energy_level?: "low" | "medium" | "high"; // Required energy level
  adhd_context_cost?: number; // Cognitive load of context switching (1-10)
  adhd_hyperfocus_compatible?: boolean; // Suitable for hyperfocus sessions
}

export interface Project {
  id: string;
  name: string;
  description?: string;
  color: string;
  created_at: string;
  deadline?: string;
  todos: string[];
  // Backend statistics (when fetched from API)
  todo_count?: number;
  completed_count?: number;
  completion_percentage?: number;
}

export interface FilterOptions {
  filter_type: "all" | "today" | "overdue" | "high_priority" | "project";
  project_id?: string;
  available_time_minutes?: number;
}

export interface FocusSuggestion {
  todo: TodoItem;
  reason: string;
  confidence: number;
}

export class ADHDTodoManager {
  private todosKey = "adhd-todos";
  private projectsKey = "adhd-projects";
  private eisenhowerMatrix = new EisenhowerMatrix();

  generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  saveTodos(todos: TodoItem[]): void {
    localStorage.setItem(this.todosKey, JSON.stringify(todos));
    // Dispatch custom event for real-time tracking
    window.dispatchEvent(new CustomEvent("adhdStorageUpdate"));
  }

  getTodos(): TodoItem[] {
    const stored = localStorage.getItem(this.todosKey);
    return stored ? JSON.parse(stored) : [];
  }

  saveProjects(projects: Project[]): void {
    localStorage.setItem(this.projectsKey, JSON.stringify(projects));
    // Dispatch custom event for real-time tracking
    window.dispatchEvent(new CustomEvent("adhdStorageUpdate"));
  }

  getProjects(): Project[] {
    const stored = localStorage.getItem(this.projectsKey);
    return stored ? JSON.parse(stored) : [];
  }

  async createTodo(args: any): Promise<{ success: boolean; todo?: TodoItem; error?: string }> {
    try {
      const todos = this.getTodos();
      const projects = this.getProjects();

      // Check for duplicate task names (case-insensitive)
      const duplicateTask = todos.find(
        todo => todo.title.toLowerCase().trim() === args.title.toLowerCase().trim()
      );

      if (duplicateTask) {
        return {
          success: false,
          error: `A task with the name "${args.title}" already exists. Please choose a different name or modify the existing task.`,
        };
      }

      const created_at = await timeService.getCurrentTimestamp();

      const newTodo: TodoItem = {
        id: this.generateId(),
        title: args.title,
        description: args.description || "",
        priority: args.priority || "medium",
        status: "pending",
        created_at,
        due_date: args.due_date,
        project_id: args.project_id,
        tags: args.tags || [],
        estimated_minutes: args.estimated_minutes,
        notes: "",
      };

      // Apply intelligent time estimation if complexity provided or estimated_minutes not set
      if (args.complexity || !args.estimated_minutes) {
        const timeEstimationMode: TimeEstimationMode = args.time_estimation_mode || 'realistic';
        const complexity = args.complexity || this.estimateComplexityFromDescription(args.title, args.description || '');
        
        // Get user's historical data
        const userHistory = timeEstimationEngine.getStoredHistory();
        
        // Determine time of day
        const now = new Date();
        const hour = now.getHours();
        const timeOfDay = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
        
        const estimationResult = timeEstimationEngine.estimateTime({
          description: `${args.title}. ${args.description || ''}`,
          complexity,
          energyLevel: this.convertEnergyToNumber(newTodo.adhd_energy_level),
          userHistory,
          mode: timeEstimationMode,
          tags: args.tags || [],
          timeOfDay
        });

        // Update todo with estimation results
        newTodo.time_estimation_mode = timeEstimationMode;
        newTodo.original_estimate = estimationResult.originalEstimate;
        newTodo.estimated_minutes = estimationResult.adjustedEstimate;
        newTodo.estimation_confidence = estimationResult.confidenceLevel;
        newTodo.suggested_breaks = estimationResult.suggestedBreaks;
        newTodo.estimation_explanation = estimationResult.explanation;
      }

      // Automatically prioritize the new todo using Eisenhower Matrix
      const priorityAnalysis = this.eisenhowerMatrix.analyzePriority(newTodo, projects);

      // Update the todo with automatic prioritization data
      newTodo.importance_level = priorityAnalysis.importanceScore;
      newTodo.urgency_level = priorityAnalysis.urgencyScore;
      newTodo.eisenhower_quadrant = priorityAnalysis.eisenhowerQuadrant;
      newTodo.auto_prioritized = true;
      newTodo.priority_confidence = priorityAnalysis.confidence;
      newTodo.priority_reasoning = priorityAnalysis.reasoning;
      newTodo.adhd_energy_level = priorityAnalysis.adhdFactors.energyLevel;
      newTodo.adhd_context_cost = priorityAnalysis.adhdFactors.contextSwitchingCost;
      newTodo.adhd_hyperfocus_compatible = priorityAnalysis.adhdFactors.hyperfocusCompatible;

      // Update the priority field based on Eisenhower quadrant if not explicitly set
      if (!args.priority) {
        switch (priorityAnalysis.eisenhowerQuadrant) {
          case 1:
            newTodo.priority = "urgent";
            break;
          case 2:
            newTodo.priority = "high";
            break;
          case 3:
            newTodo.priority = "medium";
            break;
          case 4:
            newTodo.priority = "low";
            break;
        }
      }

      todos.push(newTodo);
      this.saveTodos(todos);

      if (args.project_id) {
        const project = projects.find(p => p.id === args.project_id);
        if (project) {
          project.todos.push(newTodo.id);
          this.saveProjects(projects);
        }
      }

      // Track task creation with PostHog
      analytics.track("task_created", {
        task_type: args.project_id ? "project_task" : "standalone_task",
        estimated_duration: args.estimated_minutes || 0,
        priority: newTodo.priority,
        has_deadline: !!args.due_date,
        created_via: "text", // Default to text, can be overridden by caller
      });

      return { success: true, todo: newTodo };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
    }
  }

  async createProject(args: any): Promise<{ success: boolean; project?: Project; error?: string }> {
    try {
      const projects = this.getProjects();
      const created_at = await timeService.getCurrentTimestamp();

      const newProject: Project = {
        id: this.generateId(),
        name: args.name,
        description: args.description || "",
        color: args.color || "#3b82f6",
        created_at,
        deadline: args.deadline,
        todos: [],
      };

      projects.push(newProject);
      this.saveProjects(projects);

      return { success: true, project: newProject };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
    }
  }

  updateTodo(args: any): { success: boolean; todo?: TodoItem; error?: string } {
    try {
      const todos = this.getTodos();
      const todoId = args.todo_id || args.task_id; // Support both parameter names for backwards compatibility
      const todoIndex = todos.findIndex(t => t.id === todoId);

      if (todoIndex === -1) {
        return { success: false, error: "Todo not found" };
      }

      const todo = todos[todoIndex];
      const previousStatus = todo.status;

      if (args.status) todo.status = args.status;
      if (args.priority) todo.priority = args.priority;
      if (args.due_date) todo.due_date = args.due_date;
      if (args.project_id !== undefined) {
        // Handle project assignment - null/undefined means unassign
        todo.project_id = args.project_id || undefined;
      }
      if (args.estimated_minutes) todo.estimated_minutes = args.estimated_minutes;
      if (args.notes) {
        // Append notes with timestamp instead of replacing
        const timestamp = new Date().toLocaleString();
        todo.notes = todo.notes
          ? `${todo.notes}\n[${timestamp}] ${args.notes}`
          : `[${timestamp}] ${args.notes}`;
      }

      todos[todoIndex] = todo;
      this.saveTodos(todos);

      // Track task status changes with PostHog
      if (args.status && previousStatus !== args.status) {
        const currentTime = new Date();
        const createdTime = new Date(todo.created_at);
        const actualDuration = Math.floor(
          (currentTime.getTime() - createdTime.getTime()) / (1000 * 60)
        ); // minutes

        if (args.status === "completed") {
          // Track task completion
          analytics.track("task_completed", {
            task_id: todo.id,
            actual_duration: actualDuration,
            completion_rate: todo.estimated_minutes ? actualDuration / todo.estimated_minutes : 1,
            focus_interruptions: 0, // This could be enhanced to track interruptions
            used_timer: false, // This could be enhanced to track timer usage
          });
        } else if (
          args.status === "blocked" ||
          (previousStatus === "in_progress" && args.status === "pending")
        ) {
          // Track task abandonment
          analytics.track("task_abandoned", {
            task_id: todo.id,
            progress_percentage: previousStatus === "in_progress" ? 50 : 10, // Rough estimate
            abandonment_reason: args.notes ? "user_noted" : "status_change",
            time_of_day: currentTime.getHours(),
          });
        }
      }

      // Update project associations if project_id changed
      if (args.project_id !== undefined) {
        const projects = this.getProjects();
        let projectsChanged = false;

        // Remove todo from all projects first
        projects.forEach(project => {
          const todoIndex = project.todos.indexOf(todo.id);
          if (todoIndex !== -1) {
            project.todos.splice(todoIndex, 1);
            projectsChanged = true;
          }
        });

        // Add todo to new project if specified
        if (args.project_id) {
          const targetProject = projects.find(p => p.id === args.project_id);
          if (targetProject && !targetProject.todos.includes(todo.id)) {
            targetProject.todos.push(todo.id);
            projectsChanged = true;
          }
        }

        if (projectsChanged) {
          this.saveProjects(projects);
        }
      }

      return { success: true, todo };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
    }
  }

  updateMultipleTodos(args: {
    updates: Array<{
      task_id: string;
      status?: string;
      priority?: string;
      due_date?: string;
      estimated_minutes?: number;
      notes?: string;
    }>;
    rebalance_reason?: string;
  }): { success: boolean; updatedTodos?: TodoItem[]; error?: string } {
    try {
      const todos = this.getTodos();
      const updatedTodos: TodoItem[] = [];

      for (const update of args.updates) {
        const todoIndex = todos.findIndex(t => t.id === update.task_id);

        if (todoIndex !== -1) {
          const todo = todos[todoIndex];
          if (update.status) todo.status = update.status as TodoItem["status"];
          if (update.priority) todo.priority = update.priority as TodoItem["priority"];
          if (update.due_date) todo.due_date = update.due_date;
          if (update.estimated_minutes) todo.estimated_minutes = update.estimated_minutes;
          if (update.notes) {
            const timestamp = new Date().toLocaleString();
            const reasonNote = args.rebalance_reason ? `[${args.rebalance_reason}] ` : "";
            todo.notes = todo.notes
              ? `${todo.notes}\n[${timestamp}] ${reasonNote}${update.notes}`
              : `[${timestamp}] ${reasonNote}${update.notes}`;
          }

          todos[todoIndex] = todo;
          updatedTodos.push(todo);
        }
      }

      this.saveTodos(todos);

      return { success: true, updatedTodos };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
    }
  }

  deleteTodo(args: any): { success: boolean; error?: string } {
    try {
      const todos = this.getTodos();
      const todoId = args.todo_id || args.task_id; // Support both parameter names for backwards compatibility
      const filteredTodos = todos.filter(t => t.id !== todoId);

      if (filteredTodos.length === todos.length) {
        return { success: false, error: "Todo not found" };
      }

      this.saveTodos(filteredTodos);

      const projects = this.getProjects();
      projects.forEach(project => {
        project.todos = project.todos.filter(todoIdInProject => todoIdInProject !== todoId);
      });
      this.saveProjects(projects);

      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
    }
  }

  updateProject(args: any): { success: boolean; project?: Project; error?: string } {
    try {
      const projects = this.getProjects();
      const projectIndex = projects.findIndex(p => p.id === args.project_id);

      if (projectIndex === -1) {
        return { success: false, error: "Project not found" };
      }

      const project = projects[projectIndex];
      if (args.name) project.name = args.name;
      if (args.description !== undefined) project.description = args.description;
      if (args.color) project.color = args.color;
      if (args.deadline !== undefined) project.deadline = args.deadline;

      projects[projectIndex] = project;
      this.saveProjects(projects);

      return { success: true, project };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
    }
  }

  deleteProject(args: any): { success: boolean; error?: string } {
    try {
      const projects = this.getProjects();
      const projectToDelete = projects.find(p => p.id === args.project_id);

      if (!projectToDelete) {
        return { success: false, error: "Project not found" };
      }

      const filteredProjects = projects.filter(p => p.id !== args.project_id);
      this.saveProjects(filteredProjects);

      const todos = this.getTodos();
      todos.forEach(todo => {
        if (todo.project_id === args.project_id) {
          todo.project_id = undefined;
        }
      });
      this.saveTodos(todos);

      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
    }
  }

  private isDueToday(dueDate?: string): boolean {
    if (!dueDate) return false;
    const today = new Date();
    const due = new Date(dueDate);
    return due.toDateString() === today.toDateString();
  }

  private isOverdue(dueDate?: string): boolean {
    if (!dueDate) return false;
    const today = new Date();
    const due = new Date(dueDate);
    return due < today;
  }

  getFilteredTodos(args: FilterOptions): TodoItem[] {
    const todos = this.getTodos();

    switch (args.filter_type) {
      case "all":
        return todos;

      case "today":
        return todos.filter(t => this.isDueToday(t.due_date));

      case "overdue":
        return todos.filter(t => this.isOverdue(t.due_date));

      case "high_priority":
        return todos.filter(t => ["high", "urgent"].includes(t.priority));

      case "project":
        return args.project_id ? todos.filter(t => t.project_id === args.project_id) : todos;

      default:
        return todos;
    }
  }

  getFocusSuggestions(args: {
    available_time_minutes?: number;
    current_energy_level?: EnergyLevel;
    current_context?: string;
    prefer_hyperfocus?: boolean;
  }): FocusSuggestion[] {
    const todos = this.getTodos().filter(t => t.status === "pending");
    const projects = this.getProjects();

    // Use automatic prioritization for enhanced suggestions
    const analyzedTodos = this.eisenhowerMatrix.batchAnalyze(todos, projects);

    // Get current energy context if not provided
    const currentEnergyLevel =
      args.current_energy_level ||
      energyContextService.getCurrentContext()?.energyLevel ||
      this.estimateCurrentEnergyLevel();

    // Get ADHD-optimized recommendations
    const recommendations = this.eisenhowerMatrix.getADHDRecommendations(analyzedTodos, {
      availableTimeMinutes: args.available_time_minutes,
      currentEnergyLevel,
      preferHyperfocus:
        args.prefer_hyperfocus ||
        (args.available_time_minutes ? args.available_time_minutes > 45 : false),
    });

    return recommendations.slice(0, 5).map(todo => ({
      todo,
      reason: this.generateFocusReason(todo, args),
      confidence: (todo.priorityAnalysis.confidence || 50) / 100,
    }));
  }

  /**
   * Start a body doubling session with a specific task
   */
  startBodyDoubling(args: {
    task_id: string;
    duration_minutes?: number;
    check_in_frequency?: number;
  }): { success: boolean; session?: BodyDoublingSession; error?: string } {
    try {
      const todos = this.getTodos();
      const task = todos.find(t => t.id === args.task_id);

      if (!task) {
        return { success: false, error: "Task not found" };
      }

      const durationMinutes = args.duration_minutes || task.estimated_minutes || 25;
      const checkInFrequency =
        args.check_in_frequency || Math.max(5, Math.floor(durationMinutes / 4));

      const session = bodyDoublingService.startSession(task, durationMinutes, checkInFrequency);

      // Update task status to in_progress if it's pending
      if (task.status === "pending") {
        const updateResult = this.updateTodo({
          task_id: args.task_id,
          status: "in_progress",
          notes: `Started body doubling session at ${new Date().toLocaleTimeString()}`,
        });

        if (!updateResult.success) {
          console.warn("Failed to update task status for body doubling:", updateResult.error);
        }
      }

      return { success: true, session };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Set hyperfocus break reminders
   */
  setHyperfocusBreak(args: { break_interval?: number; break_type?: BreakType }): {
    success: boolean;
    message: string;
    error?: string;
  } {
    try {
      const breakInterval = args.break_interval || 30;
      const breakType = args.break_type || "full";

      const result = hyperfocusBreakService.setBreakReminders(breakInterval, breakType, {
        isEnabled: true,
      });

      return result;
    } catch (error) {
      return {
        success: false,
        message: "",
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Set energy context
   */
  setEnergyContext(args: {
    energy_level: EnergyLevel;
    context_description?: string;
    available_time_minutes?: number;
  }): { success: boolean; context: EnergyContext; message: string; error?: string } {
    try {
      const result = energyContextService.setEnergyContext(args.energy_level, {
        contextDescription: args.context_description,
        availableTimeMinutes: args.available_time_minutes,
      });

      return result;
    } catch (error) {
      return {
        success: false,
        context: {
          energyLevel: args.energy_level,
          timestamp: new Date(),
          contextDescription: args.context_description,
          availableTimeMinutes: args.available_time_minutes,
        },
        message: "",
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Get current body doubling session
   */
  getCurrentBodyDoublingSession(): BodyDoublingSession | null {
    return bodyDoublingService.getCurrentSession();
  }

  /**
   * Get current energy context
   */
  getCurrentEnergyContext(): EnergyContext | null {
    return energyContextService.getCurrentContext();
  }

  /**
   * Get energy insights and recommendations
   */
  getEnergyInsights() {
    return energyContextService.getEnergyInsights();
  }

  /**
   * Automatically re-prioritize all existing todos
   */
  reprioritizeAllTodos(): { success: boolean; updatedCount: number; error?: string } {
    try {
      const todos = this.getTodos();
      const projects = this.getProjects();
      let updatedCount = 0;

      const updatedTodos = todos.map(todo => {
        // Skip already completed tasks
        if (todo.status === "completed") return todo;

        const priorityAnalysis = this.eisenhowerMatrix.analyzePriority(todo, projects);

        // Update automatic prioritization fields
        const updatedTodo = {
          ...todo,
          importance_level: priorityAnalysis.importanceScore,
          urgency_level: priorityAnalysis.urgencyScore,
          eisenhower_quadrant: priorityAnalysis.eisenhowerQuadrant,
          auto_prioritized: true,
          priority_confidence: priorityAnalysis.confidence,
          priority_reasoning: priorityAnalysis.reasoning,
          adhd_energy_level: priorityAnalysis.adhdFactors.energyLevel,
          adhd_context_cost: priorityAnalysis.adhdFactors.contextSwitchingCost,
          adhd_hyperfocus_compatible: priorityAnalysis.adhdFactors.hyperfocusCompatible,
        };

        // Only update priority if it wasn't manually set recently
        if (todo.auto_prioritized !== false) {
          switch (priorityAnalysis.eisenhowerQuadrant) {
            case 1:
              updatedTodo.priority = "urgent";
              break;
            case 2:
              updatedTodo.priority = "high";
              break;
            case 3:
              updatedTodo.priority = "medium";
              break;
            case 4:
              updatedTodo.priority = "low";
              break;
          }
        }

        updatedCount++;
        return updatedTodo;
      });

      this.saveTodos(updatedTodos);
      return { success: true, updatedCount };
    } catch (error) {
      return {
        success: false,
        updatedCount: 0,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Get tasks filtered by Eisenhower quadrant with ADHD optimizations
   */
  getTasksByQuadrant(quadrant: 1 | 2 | 3 | 4, includeADHDContext?: boolean): TodoItem[] {
    const todos = this.getTodos().filter(t => t.status !== "completed");
    let filtered = todos.filter(t => t.eisenhower_quadrant === quadrant);

    if (includeADHDContext) {
      // Sort by ADHD-friendly factors
      filtered = filtered.sort((a, b) => {
        // Prioritize by confidence first
        const confidenceA = a.priority_confidence || 0;
        const confidenceB = b.priority_confidence || 0;
        if (confidenceA !== confidenceB) {
          return confidenceB - confidenceA;
        }

        // Then by context switching cost (lower is better for ADHD)
        const contextCostA = a.adhd_context_cost || 5;
        const contextCostB = b.adhd_context_cost || 5;
        return contextCostA - contextCostB;
      });
    }

    return filtered;
  }

  /**
   * Get smart focus suggestions optimized for ADHD patterns
   */
  getADHDFocusRecommendations(context: {
    availableTimeMinutes?: number;
    currentEnergyLevel?: "low" | "medium" | "high";
    preferHyperfocus?: boolean;
  }): FocusSuggestion[] {
    const todos = this.getTodos().filter(t => t.status === "pending");
    const projects = this.getProjects();

    const analyzedTodos = this.eisenhowerMatrix.batchAnalyze(todos, projects);
    const recommendations = this.eisenhowerMatrix.getADHDRecommendations(analyzedTodos, context);

    return recommendations.map(todo => {
      const quadrantInfo = this.eisenhowerMatrix.getQuadrantInfo(
        todo.priorityAnalysis.eisenhowerQuadrant
      );

      return {
        todo,
        reason: `${quadrantInfo.name}: ${todo.priorityAnalysis.reasoning.join(", ")}`,
        confidence: todo.priorityAnalysis.confidence / 100,
      };
    });
  }

  /**
   * Analyze prioritization confidence and suggest improvements
   */
  getPrioritizationInsights(): {
    lowConfidenceCount: number;
    avgConfidence: number;
    quadrantDistribution: { [key: number]: number };
    suggestions: string[];
  } {
    const todos = this.getTodos().filter(t => t.status !== "completed");
    const lowConfidenceThreshold = 60;

    let lowConfidenceCount = 0;
    let totalConfidence = 0;
    const quadrantDistribution: { [key: number]: number } = { 1: 0, 2: 0, 3: 0, 4: 0 };
    const suggestions: string[] = [];

    todos.forEach(todo => {
      const confidence = todo.priority_confidence || 0;
      totalConfidence += confidence;

      if (confidence < lowConfidenceThreshold) {
        lowConfidenceCount++;
      }

      if (todo.eisenhower_quadrant) {
        quadrantDistribution[todo.eisenhower_quadrant]++;
      }
    });

    const avgConfidence = todos.length > 0 ? totalConfidence / todos.length : 0;

    // Generate suggestions
    if (lowConfidenceCount > 0) {
      suggestions.push(
        `${lowConfidenceCount} tasks have low prioritization confidence. Consider adding more context or due dates.`
      );
    }

    if (quadrantDistribution[1] > 5) {
      suggestions.push(
        `You have ${quadrantDistribution[1]} urgent tasks. Consider delegating or eliminating some to prevent ADHD overwhelm.`
      );
    }

    if (quadrantDistribution[4] > quadrantDistribution[2]) {
      suggestions.push(
        "You have more low-priority tasks than scheduled important tasks. Consider reviewing your task list for elimination opportunities."
      );
    }

    return {
      lowConfidenceCount,
      avgConfidence: Math.round(avgConfidence),
      quadrantDistribution,
      suggestions,
    };
  }

  private estimateCurrentEnergyLevel(): "low" | "medium" | "high" {
    const hour = new Date().getHours();

    // Basic energy pattern estimation (can be enhanced with user data)
    if (hour >= 9 && hour <= 11) return "high"; // Morning peak
    if (hour >= 14 && hour <= 16) return "medium"; // Afternoon moderate
    if (hour >= 22 || hour <= 6) return "low"; // Night/early morning low

    return "medium"; // Default
  }

  private generateFocusReason(
    todo: TodoItem & { priorityAnalysis: PriorityAnalysis },
    context: {
      available_time_minutes?: number;
      current_context?: string;
      current_energy_level?: EnergyLevel;
    }
  ): string {
    const quadrantInfo = this.eisenhowerMatrix.getQuadrantInfo(
      todo.priorityAnalysis.eisenhowerQuadrant
    );
    let reason = quadrantInfo.name;

    if (context.available_time_minutes && todo.estimated_minutes) {
      if (todo.estimated_minutes <= context.available_time_minutes) {
        reason += ` (fits your ${context.available_time_minutes}min available)`;
      }
    }

    if (
      todo.priorityAnalysis.adhdFactors.hyperfocusCompatible &&
      context.available_time_minutes &&
      context.available_time_minutes > 45
    ) {
      reason += " (perfect for hyperfocus session)";
    }

    if (
      context.current_energy_level &&
      todo.priorityAnalysis.adhdFactors.energyLevel === context.current_energy_level
    ) {
      reason += " (matches your current energy level)";
    }

    return reason;
  }

  // New Task-Project Management Functions

  /**
   * Find tasks by partial or full title match
   */
  findTaskByName(args: { task_name: string; exact_match?: boolean }): {
    success: boolean;
    tasks?: TodoItem[];
    error?: string;
  } {
    try {
      const todos = this.getTodos();
      const searchTerm = args.task_name.toLowerCase().trim();

      if (args.exact_match) {
        const exactMatches = todos.filter(todo => todo.title.toLowerCase().trim() === searchTerm);
        return { success: true, tasks: exactMatches };
      } else {
        const partialMatches = todos.filter(todo => todo.title.toLowerCase().includes(searchTerm));
        return { success: true, tasks: partialMatches };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Find projects by partial or full name match
   */
  findProjectByName(args: { project_name: string; exact_match?: boolean }): {
    success: boolean;
    projects?: Project[];
    error?: string;
  } {
    try {
      const projects = this.getProjects();
      const searchTerm = args.project_name.toLowerCase().trim();

      if (args.exact_match) {
        const exactMatches = projects.filter(
          project => project.name.toLowerCase().trim() === searchTerm
        );
        return { success: true, projects: exactMatches };
      } else {
        const partialMatches = projects.filter(project =>
          project.name.toLowerCase().includes(searchTerm)
        );
        return { success: true, projects: partialMatches };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Move a task to a different project with intelligent lookup
   */
  moveTaskToProject(args: { task_identifier: string; project_identifier?: string }): {
    success: boolean;
    task?: TodoItem;
    project?: Project;
    message?: string;
    error?: string;
  } {
    try {
      const todos = this.getTodos();
      const projects = this.getProjects();

      // Find the task
      let targetTask: TodoItem | undefined;

      // First try to find by ID
      targetTask = todos.find(t => t.id === args.task_identifier);

      // If not found, search by title
      if (!targetTask) {
        const taskSearchResults = this.findTaskByName({
          task_name: args.task_identifier,
          exact_match: false,
        });

        if (
          taskSearchResults.success &&
          taskSearchResults.tasks &&
          taskSearchResults.tasks.length > 0
        ) {
          targetTask = taskSearchResults.tasks[0]; // Use first match
        }
      }

      if (!targetTask) {
        return {
          success: false,
          error: `Task "${args.task_identifier}" not found. Please check the task name or try being more specific.`,
        };
      }

      // Handle project assignment
      let targetProjectId: string | undefined;
      let targetProject: Project | undefined;

      if (
        !args.project_identifier ||
        args.project_identifier.toLowerCase() === "none" ||
        args.project_identifier.toLowerCase() === "unassigned"
      ) {
        // Unassign from project
        targetProjectId = undefined;
      } else {
        // Find the project
        targetProject = projects.find(p => p.id === args.project_identifier);

        // If not found, search by name
        if (!targetProject) {
          const projectSearchResults = this.findProjectByName({
            project_name: args.project_identifier,
            exact_match: false,
          });

          if (
            projectSearchResults.success &&
            projectSearchResults.projects &&
            projectSearchResults.projects.length > 0
          ) {
            targetProject = projectSearchResults.projects[0]; // Use first match
          }
        }

        if (!targetProject) {
          return {
            success: false,
            error: `Project "${args.project_identifier}" not found. Please check the project name or create the project first.`,
          };
        }

        targetProjectId = targetProject.id;
      }

      // Update the task
      const updateResult = this.updateTodo({
        todo_id: targetTask.id,
        project_id: targetProjectId,
      });

      if (!updateResult.success) {
        return updateResult;
      }

      // Generate success message
      let message: string;
      if (targetProject) {
        message = `Task "${targetTask.title}" has been moved to project "${targetProject.name}".`;
      } else {
        message = `Task "${targetTask.title}" has been unassigned from all projects.`;
      }

      return {
        success: true,
        task: updateResult.todo,
        project: targetProject,
        message,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Move multiple tasks to the same project
   */
  batchMoveTasksToProject(args: { task_identifiers: string[]; project_identifier: string }): {
    success: boolean;
    moved?: Array<{ task: TodoItem; project?: Project }>;
    failed?: Array<{ identifier: string; error: string }>;
    message?: string;
    error?: string;
  } {
    try {
      const moved: Array<{ task: TodoItem; project?: Project }> = [];
      const failed: Array<{ identifier: string; error: string }> = [];

      for (const taskIdentifier of args.task_identifiers) {
        const result = this.moveTaskToProject({
          task_identifier: taskIdentifier,
          project_identifier: args.project_identifier,
        });

        if (result.success && result.task) {
          moved.push({
            task: result.task,
            project: result.project,
          });
        } else {
          failed.push({
            identifier: taskIdentifier,
            error: result.error || "Unknown error",
          });
        }
      }

      const totalTasks = args.task_identifiers.length;
      const successCount = moved.length;
      const failureCount = failed.length;

      let message: string;
      if (successCount === totalTasks) {
        const projectName = moved[0]?.project?.name || "unassigned";
        message = `Successfully moved all ${successCount} tasks to ${projectName === "unassigned" ? "unassigned tasks" : `project "${projectName}"`}.`;
      } else if (successCount > 0) {
        message = `Moved ${successCount} out of ${totalTasks} tasks. ${failureCount} tasks could not be moved.`;
      } else {
        message = `Failed to move any tasks. Please check the task and project names.`;
      }

      return {
        success: successCount > 0,
        moved: moved.length > 0 ? moved : undefined,
        failed: failed.length > 0 ? failed : undefined,
        message,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Helper method to estimate task complexity from description
   */
  private estimateComplexityFromDescription(title: string, description: string): number {
    const text = `${title} ${description}`.toLowerCase();
    let complexity = 5; // Default medium complexity

    // Simple task indicators
    if (text.includes('check') || text.includes('quick') || text.includes('simple') || text.includes('fast')) {
      complexity = Math.max(1, complexity - 2);
    }

    // Complex task indicators
    if (text.includes('research') || text.includes('analyze') || text.includes('design') || text.includes('plan')) {
      complexity = Math.min(10, complexity + 2);
    }

    // Very complex indicators
    if (text.includes('implement') || text.includes('develop') || text.includes('create system') || text.includes('architecture')) {
      complexity = Math.min(10, complexity + 3);
    }

    // Learning/new skill indicators
    if (text.includes('learn') || text.includes('tutorial') || text.includes('new') || text.includes('first time')) {
      complexity = Math.min(10, complexity + 1);
    }

    return Math.max(1, Math.min(10, complexity));
  }

  /**
   * Convert ADHD energy level string to number
   */
  private convertEnergyToNumber(energyLevel?: "low" | "medium" | "high"): number {
    switch (energyLevel) {
      case 'low': return 1;
      case 'medium': return 3;
      case 'high': return 5;
      default: return 3;
    }
  }

  /**
   * Mark task as completed and record time estimation accuracy
   */
  async completeTaskWithTimeTracking(
    todoId: string, 
    actualMinutes: number,
    distractionCount?: number
  ): Promise<{ success: boolean; todo?: TodoItem; error?: string }> {
    try {
      const todos = this.getTodos();
      const todoIndex = todos.findIndex(t => t.id === todoId);
      
      if (todoIndex === -1) {
        return { success: false, error: "Task not found" };
      }

      const todo = todos[todoIndex];
      
      // Record time estimation accuracy if we have estimation data
      if (todo.estimated_minutes && todo.time_estimation_mode) {
        const complexity = this.estimateComplexityFromDescription(todo.title, todo.description || '');
        const energyLevel = this.convertEnergyToNumber(todo.adhd_energy_level);
        
        timeEstimationEngine.recordCompletion(
          todo.estimated_minutes,
          actualMinutes,
          complexity,
          energyLevel,
          true, // Task was completed
          distractionCount
        );
      }

      // Update the todo
      todo.status = 'completed';
      todo.actual_minutes = actualMinutes;
      todos[todoIndex] = todo;
      
      this.saveTodos(todos);

      // Track completion with analytics
      analytics.track("task_completed", {
        task_duration: actualMinutes,
        estimated_duration: todo.estimated_minutes || 0,
        estimation_accuracy: todo.estimated_minutes ? actualMinutes / todo.estimated_minutes : 1,
        priority: todo.priority,
        has_deadline: !!todo.due_date,
        distraction_count: distractionCount || 0
      });

      return { success: true, todo };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
    }
  }

  /**
   * Get time estimation accuracy statistics
   */
  getTimeEstimationStats() {
    return timeEstimationEngine.getAccuracyStats();
  }

  /**
   * Update task time estimation mode
   */
  updateTaskTimeEstimation(
    todoId: string, 
    mode: TimeEstimationMode,
    complexity?: number
  ): { success: boolean; todo?: TodoItem; error?: string } {
    try {
      const todos = this.getTodos();
      const todoIndex = todos.findIndex(t => t.id === todoId);
      
      if (todoIndex === -1) {
        return { success: false, error: "Task not found" };
      }

      const todo = todos[todoIndex];
      const taskComplexity = complexity || this.estimateComplexityFromDescription(todo.title, todo.description || '');
      
      // Get user's historical data
      const userHistory = timeEstimationEngine.getStoredHistory();
      
      // Determine time of day
      const now = new Date();
      const hour = now.getHours();
      const timeOfDay = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
      
      const estimationResult = timeEstimationEngine.estimateTime({
        description: `${todo.title}. ${todo.description || ''}`,
        complexity: taskComplexity,
        energyLevel: this.convertEnergyToNumber(todo.adhd_energy_level),
        userHistory,
        mode,
        tags: todo.tags,
        timeOfDay
      });

      // Update todo with new estimation
      todo.time_estimation_mode = mode;
      todo.original_estimate = estimationResult.originalEstimate;
      todo.estimated_minutes = estimationResult.adjustedEstimate;
      todo.estimation_confidence = estimationResult.confidenceLevel;
      todo.suggested_breaks = estimationResult.suggestedBreaks;
      todo.estimation_explanation = estimationResult.explanation;

      todos[todoIndex] = todo;
      this.saveTodos(todos);

      return { success: true, todo };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
    }
  }
}
